import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { AvatarUpload } from "@/components/profile/AvatarUpload";
import { ProfileForm } from "@/components/profile/ProfileForm";
import { PasswordForm } from "@/components/profile/PasswordForm";
import { useProfileData } from "@/hooks/useProfileData";
import { useProfileForm } from "@/hooks/useProfileForm";

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string>("");
  const [firstName, setFirstName] = useState("");

  const {
    username, setUsername,
    bio, setBio,
    avatarUrl, setAvatarUrl,
    nationality, setNationality,
    creatorTypes, setCreatorTypes,
    userRole,
    getProfile,
    isLoading: profileLoading,
    error: profileError
  } = useProfileData();

  const { handleUsernameChange: handleUsernameChangeBase, updateProfile: updateProfileBase } = useProfileForm();

  const initializeProfile = useCallback(async () => {
    console.log("Starting profile initialization");
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log("Session check result:", { 
        hasSession: !!session, 
        sessionError: sessionError?.message 
      });
      
      if (sessionError) {
        console.error("Session error:", sessionError);
        throw sessionError;
      }
      
      if (!session) {
        console.log("No session found, redirecting to login");
        toast.error("Please sign in to access your profile");
        navigate("/login");
        return;
      }

      console.log("Setting user ID:", session.user.id);
      setUserId(session.user.id);
      console.log("Calling getProfile with ID:", session.user.id);
      await getProfile(session.user.id);

      // Fetch first name
      const { data: profileData } = await supabase
        .from('profiles')
        .select('first_name')
        .eq('id', session.user.id)
        .single();

      if (profileData?.first_name) {
        setFirstName(profileData.first_name);
      }

    } catch (error) {
      console.error("Profile initialization error:", error);
      toast.error("Authentication error. Please log in again.");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [navigate, getProfile]);

  useEffect(() => {
    initializeProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/login");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initializeProfile]);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleUsernameChangeBase(e.target.value, setUsername);
  };

  const handleCreatorTypeClick = (type: string) => {
    setCreatorTypes(prevTypes => {
      if (!prevTypes) return [type];
      
      const currentTypes = [...prevTypes];
      if (currentTypes.includes(type)) {
        return currentTypes.filter(t => t !== type);
      } else {
        if (currentTypes.length < 2) {
          return [...currentTypes, type];
        }
        toast.error("You can only select up to 2 creator types");
        return currentTypes;
      }
    });
  };

  const updateProfile = async () => {
    const { data: { session } } = await supabase.auth.getSession();
      
    if (!session) {
      toast.error("Please sign in to update your profile");
      navigate("/login");
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          username,
          bio,
          avatar_url: avatarUrl,
          nationality,
          creator_type: creatorTypes,
          first_name: firstName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', session.user.id);

      if (error) throw error;
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error("Error updating profile");
    }
  };

  if (loading || profileLoading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <DashboardSidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 flex items-center justify-center">
              <div className="animate-pulse text-lg">Loading profile...</div>
            </main>
            <Footer />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (profileError) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full">
          <DashboardSidebar />
          <div className="flex-1 flex flex-col">
            <Header />
            <main className="flex-1 flex items-center justify-center">
              <div className="text-red-500">{profileError}</div>
            </main>
            <Footer />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <DashboardSidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <main className="flex-1">
            <div className="container max-w-2xl mx-auto py-12 px-4 space-y-8">
              <h1 className="text-3xl font-bold mb-8">Edit Profile</h1>
              <div className="space-y-6">
                <AvatarUpload
                  avatarUrl={avatarUrl}
                  setAvatarUrl={setAvatarUrl}
                  username={username}
                />
                <ProfileForm
                  username={username}
                  setUsername={setUsername}
                  firstName={firstName}
                  setFirstName={setFirstName}
                  bio={bio}
                  setBio={setBio}
                  nationality={nationality}
                  setNationality={setNationality}
                  creatorTypes={creatorTypes}
                  setCreatorTypes={setCreatorTypes}
                  userRole={userRole}
                  updateProfile={updateProfile}
                  handleUsernameChange={handleUsernameChange}
                  handleCreatorTypeClick={handleCreatorTypeClick}
                />
                <PasswordForm />
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
};
